import {Button, cn} from '@commercial-website/ui'
import {m as motion, AnimatePresence} from 'motion/react'
import React from 'react'
import styled, {css} from 'styled-components'

import RichText, {hasRichText} from '@/components/rich-text'
import type {Props as RichTextProps} from '@/components/rich-text'
import {text} from '@/components/text'
import {useEmailValidationErrorMessage} from '@/hooks/use-email-validation-error-message'
import useHasMounted from '@/hooks/use-has-mounted'
import useSignup from '@/hooks/use-signup'
import type {Args as UseSignupArgs} from '@/hooks/use-signup'
import {colors, fonts, tokens} from '@/theme'
import type {FormConfig} from '@/types/consents'
import mq from '@/utils/media-query'

import {useGlobalStrings} from '@helios/website/src/providers/global-strings'
import {focusStyles} from '@helios/website/src/styles/utils'

type OmittedInputProps = 'css' | 'ref' | 'type' | 'required' | 'value' | 'onChange'

type InputProps = Omit<JSX.IntrinsicElements['input'], OmittedInputProps>

export type SimpleSignupFormProps = InputProps & {
    submitText: string
    ariaLabel?: string
    errorText?: string | React.ReactNode
    disclaimerCopy?: RichTextProps['content']
    withBorder?: boolean
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>
    handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    email: string
    loading: boolean
    error: Error | null
    withErrorAnimation?: boolean
    stacked?: boolean
    dataTestId?: string
    className?: string
}

export const SimpleSignupForm = ({
    submitText,
    errorText,
    disclaimerCopy,
    withBorder,
    handleSubmit,
    handleEmailChange,
    email,
    loading,
    error,
    stacked,
    ariaLabel,
    dataTestId,
    placeholder,
    className,
    withErrorAnimation = false,
}: SimpleSignupFormProps) => {
    const hasMounted = useHasMounted()
    const globalStrings = useGlobalStrings()
    const label = ariaLabel ?? globalStrings.yourEmailLabel

    return (
        <FormWrapper className={className}>
            <Form
                onSubmit={handleSubmit}
                $stacked={stacked}
                noValidate
                animate={error && withErrorAnimation ? 'shake' : 'idle'}
                variants={{
                    idle: {x: 0},
                    shake: {
                        x: [-4, 4, -2, 2, 0],
                        transition: {
                            duration: 0.3,
                        },
                    },
                }}
            >
                <EmailInput
                    disabled={!hasMounted}
                    placeholder={placeholder}
                    type="email"
                    $withBorder={withBorder}
                    aria-label={label}
                    onChange={handleEmailChange}
                    value={email}
                    required
                    $stacked={stacked}
                    {...(dataTestId && {
                        'data-testid': `simple-signup-form-email-input-${dataTestId}`,
                    })}
                />

                <Button
                    type="submit"
                    className={cn(
                        'm-0 max-w-none px-6 py-4.5',
                        !withBorder && 'border-transparent',
                        !stacked && 'xs:rounded-l-none',
                    )}
                    disabled={loading || !hasMounted}
                    data-testid={
                        dataTestId ? `simple-signup-form-submit-button-${dataTestId}` : undefined
                    }
                >
                    {submitText}
                </Button>
            </Form>

            <AnimatePresence>
                {error && (
                    <Error
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.1}}
                        stacked={stacked}
                        {...(dataTestId && {
                            'data-testid': `simple-signup-form-error-${dataTestId}`,
                        })}
                    >
                        {errorText}
                    </Error>
                )}
            </AnimatePresence>

            {hasRichText(disclaimerCopy) && (
                <RichText $variant="label-base" content={disclaimerCopy} />
            )}
        </FormWrapper>
    )
}

const breakpoint = 'xs'

const FormWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing12};
    width: 100%;
    position: relative;
`

const Form = styled(motion.form)<{$stacked: Props['stacked']}>(({$stacked}) => {
    const inlineStyles = css`
        ${mq.min(breakpoint)} {
            gap: 0;
            grid-template-columns: 1fr auto;
        }
    `

    return css`
        display: grid;
        width: 100%;
        grid-template-columns: 1fr;
        gap: ${tokens.spacing12};

        ${!$stacked && inlineStyles}
    `
})

const formControlStyles = css`
    max-width: none;
    padding: ${tokens.spacing18} ${tokens.spacing24};
    margin: 0;
    font-size: ${tokens.fontLarge};
    line-height: 1;
    border: 1px solid;
    outline: none;
    box-shadow: none;
    appearance: none;
`

const noBorderStyles = css`
    border-color: transparent;
`

type FormControlProps = {$withBorder: Props['withBorder']; $stacked: Props['stacked']}

const EmailInput = styled.input<FormControlProps>(({$withBorder, $stacked}) => {
    const inlineStyles = css`
        ${mq.min(breakpoint)} {
            &,
            &:focus {
                text-align: left;
                border-right: none;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }
        }
    `

    return css`
        ${formControlStyles};
        width: 100%;
        font-family: ${fonts.families.secondary};
        color: ${colors.textInput.signup.foreground};
        background: ${colors.textInput.signup.background};
        border-color: ${colors.textInput.signup.border};
        border-radius: ${({theme}) => theme.radii.arc8};

        &:focus {
            ${focusStyles()}
        }

        &::placeholder {
            color: ${colors.text.placeholder};
            opacity: 1;
        }

        ${mq.min('xs')} {
            border-radius: ${({theme}) => theme.radii.circle};
        }

        ${!$withBorder && noBorderStyles}
        ${!$stacked && inlineStyles}
    `
})

type ErrorProps = Pick<Props, 'stacked'>

const Error = styled(motion.p)<ErrorProps>(({stacked}) => {
    const inlineStyles = css`
        ${mq.min(breakpoint)} {
            text-align: left;
        }
    `

    return css`
        position: absolute;
        bottom: -26px;
        left: ${({theme}) => theme.spacings.s24};
        text-align: center;
        ${text({$variant: 'label-base'})}
        color: ${({theme}) => theme.tokens.red700};

        button {
            padding: unset;
            font: inherit;
            color: inherit;
            text-decoration: underline;
            cursor: pointer;
            background: unset;
            border: unset;
        }

        ${!stacked && inlineStyles}
    `
})

export interface Props {
    placeholder: string
    submitText: string
    disclaimerCopy?: RichTextProps['content']
    formConfig: FormConfig
    errorText?: string
    withBorder?: boolean
    signupOptions?: UseSignupArgs
    stacked?: boolean
    ariaLabel?: string
    dataTestId?: string
}

const SimpleSignupFormContainer = ({signupOptions, formConfig, ...props}: Props) => {
    const signupProps = useSignup(
        signupOptions || {
            form: 'Front',
        },
        formConfig.formId,
    )
    const errorMessage = useEmailValidationErrorMessage(signupProps.error)

    return <SimpleSignupForm {...props} {...signupProps} errorText={errorMessage} />
}

export default SimpleSignupFormContainer
